import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { band, linear, niceMax, ticks } from '../chart-scales';
import type { SeriesModel } from '../analytics-charts.model';
import { useElementWidth } from '../use-element-width';
import { Bar, pctText } from './svg-parts';

// a) Activos y persistencia: dos paneles apilados con el MISMO eje de períodos.
// Sin doble eje (PM).

export function SeriesChart({ model, minN }: { model: SeriesModel; minN: number }) {
  const { t } = useTranslation('sis');
  const theme = useTheme();
  const { ref, width: W } = useElementWidth<HTMLDivElement>();
  const ink = theme.palette.text.secondary;
  const grid = theme.palette.divider;
  const bar = theme.palette.primary.main;
  const paper = theme.palette.background.paper;
  const pts = model.points;
  const narrow = W < 560;
  const m = { l: 44, r: 12 };
  const top1 = 24,
    h1 = 150,
    gap = 46,
    h2 = 120,
    H = top1 + h1 + gap + h2 + 30;
  const top2 = top1 + h1 + gap;
  const bx = band(pts.length, m.l, W - m.r, narrow ? 0.25 : 0.3);
  const { max, step } = niceMax(Math.max(1, ...pts.map((p) => p.active)));
  const y1 = linear(0, max, top1 + h1, top1);
  const y2 = linear(0, 1, top2 + h2, top2);
  const every = Math.max(1, Math.ceil(pts.length / Math.max(1, Math.floor((W - m.l) / 70))));

  // La línea se corta donde no hay valor (suprimido o en curso): nunca un cero.
  let path = '';
  let open = false;
  pts.forEach((p, i) => {
    if (p.persistence.kind !== 'value') {
      open = false;
      return;
    }
    path += `${open ? 'L' : 'M'}${bx.center(i)},${y2(p.persistence.value)}`;
    open = true;
  });

  return (
    <div ref={ref}>
      <svg
        width="100%"
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={t('reports.analytics.series.title')}
      >
        <text x={0} y={12} fontSize={12} fontWeight={600} fill={theme.palette.text.primary}>
          {t('reports.analytics.series.active')}
        </text>
        {ticks(max, step).map((v) => (
          <g key={`a${v}`}>
            <line x1={m.l} x2={W - m.r} y1={y1(v)} y2={y1(v)} stroke={grid} />
            <text x={m.l - 6} y={y1(v) + 4} fontSize={11} textAnchor="end" fill={ink}>
              {v}
            </text>
          </g>
        ))}
        {pts.map((p, i) => (
          <Bar key={p.key} x={bx.x(i)} y={y1(p.active)} w={bx.width} base={top1 + h1} fill={bar}>
            <title>
              {`${p.label}: ${t('reports.analytics.series.activeTip', { count: p.active, auditors: p.auditorsOnly })}`}
            </title>
          </Bar>
        ))}
        <text x={0} y={top2 - 14} fontSize={12} fontWeight={600} fill={theme.palette.text.primary}>
          {t('reports.analytics.series.persistence')}
        </text>
        {[0, 0.25, 0.5, 0.75, 1].map((v) => (
          <g key={`p${v}`}>
            <line x1={m.l} x2={W - m.r} y1={y2(v)} y2={y2(v)} stroke={grid} />
            <text x={m.l - 6} y={y2(v) + 4} fontSize={11} textAnchor="end" fill={ink}>
              {pctText(v)}
            </text>
          </g>
        ))}
        <path d={path} fill="none" stroke={bar} strokeWidth={2} strokeLinejoin="round" />
        {pts.map((p, i) => {
          const x = bx.center(i);
          const q = p.persistence;
          if (q.kind === 'value') {
            return (
              <circle
                key={p.key}
                cx={x}
                cy={y2(q.value)}
                r={4}
                fill={bar}
                stroke={paper}
                strokeWidth={2}
              >
                <title>{`${p.label}: ${pctText(q.value)} (${q.num}/${q.den})`}</title>
              </circle>
            );
          }
          const label =
            q.kind === 'suppressed'
              ? t('reports.analytics.suppressed', { min: minN })
              : t('reports.analytics.series.pending');
          return (
            <text
              key={p.key}
              x={x}
              y={y2(0.5)}
              fontSize={10.5}
              fontStyle="italic"
              textAnchor="middle"
              fill={ink}
            >
              {label}
            </text>
          );
        })}
        {pts.map((p, i) =>
          i % every === 0 ? (
            <text
              key={`x${p.key}`}
              x={bx.center(i)}
              y={top2 + h2 + 18}
              fontSize={10.5}
              textAnchor="middle"
              fill={ink}
            >
              {p.label}
            </text>
          ) : null,
        )}
      </svg>
    </div>
  );
}
